import { Children, cloneElement, ReactElement, ReactNode, useEffect, useState } from 'react';

import { FormField } from '../form-field/FormField';
import { FormControl } from '../models/FormControl';

import { CheckboxProps, CHECKBOX_CONTEXT } from './common';

import './CheckboxGroup.light.scss';
import './CheckboxGroup.dark.scss';

interface Props<T> {
  /**
   * The form control model that should contain the value of the checked checkboxes
   */
  formControl: FormControl<T[]>;
  children: ReactNode | ReactNode[];
  /**
   * The optional label to render for the checkbox group
   */
  label?: string;
  className?: string;
  hint?: string;
}

export function CheckboxGroup<T>(props: Props<T>) {
  const formControl = props.formControl;
  const [checkboxValueSlots, setCheckboxValueSlots] = useState<T[]>([]);
  const toggle = (value: T, checkboxIndex: number) => {
    const updatedCheckboxValueSlots = [...checkboxValueSlots];
    updatedCheckboxValueSlots[checkboxIndex] = updatedCheckboxValueSlots[checkboxIndex] !== null ? null : value;
    setCheckboxValueSlots(updatedCheckboxValueSlots);
    props.formControl.setValue(updatedCheckboxValueSlots.filter(selectedValue => selectedValue !== null));
  };

  useEffect(() => {
    const selectedValues = Array.isArray(formControl.getValue()) ? formControl.getValue() : [];
    const updatedCheckboxValueSlots = [] as T[];
    Children.forEach(props.children, (child: ReactElement<CheckboxProps<T>>, i) => {
      updatedCheckboxValueSlots[i] = selectedValues.includes(child.props.value) ? child.props.value : null;
    });
    setCheckboxValueSlots(updatedCheckboxValueSlots);
  }, [props.children]);

  return (
    <FormField
      className={`checkbox-group${props.className ? ' ' + props.className : ''}`}
      label={props.label}
      hint={props.hint}
      formControl={formControl}>
      <CHECKBOX_CONTEXT.Provider value={{ toggle }}>
        {
          Children.map(props.children, (child: ReactElement<CheckboxProps<T>>, i) => cloneElement(
            child,
            {
              index: i,
              checked: Boolean(checkboxValueSlots.includes(child.props.value))
            }))
        }
      </CHECKBOX_CONTEXT.Provider>
    </FormField>
  );

}
